import { useState } from 'react';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import type { Device } from '@eon/types';
import { Search } from 'lucide-react';
import { ConnectorStatusBadge, DeviceStatusBadge } from '@/components/status/StatusBadges';

interface RegisterDeviceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RegisterDeviceDialog({ open, onOpenChange }: RegisterDeviceDialogProps) {
  const { t } = useTranslation();
  const [serial, setSerial] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [checked, setChecked] = useState<Device | null>(null);
  const check = hooks.useOmDeviceCheck();
  const own = hooks.useOmDeviceOwn();

  const onCheck = async () => {
    const trimmed = serial.trim();
    if (!trimmed) return;
    try {
      const device = await check.mutateAsync(trimmed);
      setChecked(device);
      setDisplayName(device.displayName?.trim() ?? '');
      toast.success(t('portal.devices.serialFound'));
    } catch (err) {
      setChecked(null);
      toast.error(resolveApiError(err, t));
    }
  };

  const onClaim = async () => {
    if (!checked?.id) return;
    try {
      await own.mutateAsync({
        deviceId: checked.id,
        displayName: displayName.trim() || undefined,
      });
      toast.success(t('portal.devices.claimed'));
      setSerial('');
      setDisplayName('');
      setChecked(null);
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setSerial('');
          setDisplayName('');
          setChecked(null);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('portal.devices.registerTitle')}</DialogTitle>
          <DialogDescription>{t('portal.devices.registerDescription')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="device-serial">{t('device.serialNumber')}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                <Input
                  id="device-serial"
                  className="pl-9 font-mono"
                  value={serial}
                  onChange={(e) => {
                    setSerial(e.target.value);
                    setChecked(null);
                  }}
                />
              </div>
              <Button type="button" variant="outline" onClick={onCheck} disabled={check.isPending || !serial.trim()}>
                {t('portal.devices.serialCheck')}
              </Button>
            </div>
          </div>
          {checked ? (
            <div className="bg-muted/40 space-y-2 rounded-lg border p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{checked.displayName?.trim() || checked.serialNumber}</span>
                <DeviceStatusBadge status={checked.status} />
              </div>
              <p className="text-muted-foreground font-mono text-xs">{checked.serialNumber}</p>
              {(checked.vendor || checked.model) && (
                <p className="text-muted-foreground text-xs">
                  {[checked.vendor, checked.model].filter(Boolean).join(' · ')}
                </p>
              )}
              <div className="flex flex-wrap gap-1">
                {(checked.connectors ?? []).map((c) => (
                  <ConnectorStatusBadge key={c.id} id={c.id} status={c.status} />
                ))}
              </div>
              <div className="space-y-1.5 pt-2">
                <Label htmlFor="device-display-name">{t('portal.devices.displayName')}</Label>
                <Input
                  id="device-display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button type="button" onClick={onClaim} disabled={!checked || own.isPending}>
            {t('portal.devices.claim')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
